import { createGetKcContext } from "keycloakify/login"

export type KcContextExtension =
    // WARNING: It's important to keep in sync the extraThemeProperties declared in the package.json and this type definition.
    | { pageId: "login.ftl"; extraThemeProperties: { foo: string } }
    | {
          pageId: "login-config-totp.ftl"
          totp: { applicationLinks: Record<string, string[]> }
      }
    // NOTE: register.ftl is deprecated in favor of register-user-profile.ftl
    
    | { pageId: "register-user-profile.ftl"; termsAcceptanceRequired: boolean }
    | { pageId: "choice-form.ftl"; otpConfigured: boolean; showError: boolean; }
    | { pageId: "webauthn-register.ftl"; signatureAlgorithms: number[], challenge: string, userid: string; username: string; rpEntityName: string; rpId: string;  attestationConveyancePrefrence: string; authenticatorAttachment: string;  requireResidentKey: string; userVerificationRequirement: string; createTimeout: number; excludeCredentialsId: string; isSetRetry: string}

//NOTE: In most of the cases you do not need to overload the KcContext, you can
// just call createGetKcContext(...) without type arguments.
// You want to overload the KcContext only if:
// - You have custom plugins that add some values to the context (like https://github.com/micedre/keycloak-mail-whitelisting that adds authorizedMailDomains)
// - You want to add support for extra pages that are not yey featured by default, see: https://docs.keycloakify.dev/contributing#adding-support-for-a-new-page
export const { getKcContext } = createGetKcContext<KcContextExtension>({
    mockData: [
        {
            pageId: "login.ftl",
            locale: {
                //When we test the login page we do it in french
                currentLanguageTag: "en",
            },
            //Uncomment the following line for hiding the Alert message
            //"message": undefined
            //Uncomment the following line for showing an Error message
            //message: { type: "error", summary: "This is an error" }
        },
		{
			pageId: "choice-form.ftl",
			otpConfigured: false,
            showError: true,
		},
        {
            //NOTE: You will either use register.ftl (legacy) or register-user-profile.ftl, not both
            pageId: "register-user-profile.ftl",
            client: {
                clientId: "example_theme",
                name: "Keycloakify",
                description: "",
                attributes: {
                    "access.token.lifespan": "60",
                    "client.secret.creation.time": "1701432798",
                    login_theme: "smdh-theme",
                    "post.logout.redirect.uris":
                        "https://www.keycloak.org/app*## http://localhost*",
                    "oauth2.device.authorization.grant.enabled": "false",
                    "backchannel.logout.revoke.offline.tokens": "false",
                    "use.refresh.tokens": "true",
                    "oidc.ciba.grant.enabled": "false",
                    "backchannel.logout.session.required": "true",
                    "client_credentials.use_refresh_token": "false",
                    "client.offline.session.idle.timeout": "60",
                    "tls.client.certificate.bound.access.tokens": "false",
                    "require.pushed.authorization.requests": "false",
                    "acr.loa.map": "{}",
                    "display.on.consent.screen": "true",
                    "client.offline.session.max.lifespan": "60",
                    "client.session.max.lifespan": "60",
                    "token.response.type.bearer.lower-case": "false",
                    "client.session.idle.timeout": "60",
                },
            },
            realm: {
                displayName: "SMDH",
                displayNameHtml: "SMDH",
                internationalizationEnabled: false,
                name: "example",
                registrationEmailAsUsername: true,
            },
            url: {
                loginAction:
                    "http://localhost:8080/realms/example/login-actions/authenticate?session_code=ZvTkQeM8zSVQS8Y7NU_cE4UbgB3tZPxaK8qKJ6if79g&execution=35d2ef65-eb98-4394-a4e0-8400afa9cb0e&client_id=example_theme&tab_id=L9XrKI5ILGM",
                loginUrl:
                    "/realms/example/login-actions/authenticate?client_id=example_theme&tab_id=L9XrKI5ILGM",
                registrationAction:
                    "http://localhost:8080/realms/example/login-actions/authenticate?session_code=ZvTkQeM8zSVQS8Y7NU_cE4UbgB3tZPxaK8qKJ6if79g&execution=35d2ef65-eb98-4394-a4e0-8400afa9cb0e&client_id=example_theme&tab_id=L9XrKI5ILGM",
                loginRestartFlowUrl:
                    "/realms/example/login-actions/restart?client_id=example_theme&tab_id=L9XrKI5ILGM&skip_logout=false",
            },
            locale: {
                currentLanguageTag: "en",
            },
            recaptchaRequired: true,
            recaptchaSiteKey: "6LdXSyopAAAAAPQiPeeV04TJwHRfVC0NFqdXogsl",
            // termsAcceptanceRequired: true,
            profile: {
                attributes: [
                    {
                        validators: {
                            pattern: {
                                pattern: "^[a-zA-Z0-9]+$",
                                "ignore.empty.value": true,
                                // eslint-disable-next-line no-template-curly-in-string
                                "error-message": "${alphanumericalCharsOnly}",
                            },
                        },
                        //NOTE: To override the default mock value
                        value: undefined,
                        name: "username",
                    },
                ],
            },
        },
        {
            pageId: "login-config-totp.ftl",
            totp: {
                supportedApplications: ["Google Authenticator"],
                applicationLinks: {
                    "Google Authenticator": [
                        "otpauth://totp/Google%20Authenticator:admin%40example.com?secret=JBSWY3DPEHPK3PXP",
                        "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
                    ],
                    totpAppMicrosoftAuthenticatorName: [
                        "otpauth://totp/Microsoft%20Authenticator:admin%40example.com?secret=JBSWY3DPEHPK3PXP",
                        "https://play.google.com/store/apps/details?id=com.azure.authenticator",
                    ],
                    totpAppFreeOTPName: [
                        "otpauth://totp/FreeOTP:admin%40example.com?secret=JBSWY3DPEHPK3PXP",
                        "https://play.google.com/store/apps/details?id=org.fedorahosted.freeotp",
                    ],
                },
                qrUrl: "https://chart.googleapis.com/chart?chs=200x200&chld=M|0&cht=qr&chl=otpauth://totp/Google%20Authenticator%3Aadmin%40example.com%3Fsecret%3DJBSWY3DPEHPK3PXP",
                totpSecretQrCode:
                    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANIAAAAgCAYAAAD9ZRYQAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH5AIVEB4RgA6XZQAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVAgb24gYSBNYWOVZQAAABl0RVh0Q29tbWVudABDcmVhdGVkIHdpdGggR0lNUCBvbiBhIE1hY5VlAAAAhElEQVR42u3BMQEAAADCoPVPbQo/oAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA",
            },
        },
    ],
})

export const { kcContext } = getKcContext({
    // Uncomment to test the login page for development.
    //mockPageId: "login.ftl",
})

export type KcContext = NonNullable<
    ReturnType<typeof getKcContext>["kcContext"] & {shouldShowAnotherStep?: boolean}
>
